import type { ComponentProps, ReactNode } from 'react';
import { Radio, RadioGroup, Switch as HeadlessSwitch } from '@headlessui/react';
import { cn } from '@/lib/cn';

export function Switch({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <HeadlessSwitch
      checked={checked}
      onChange={onChange}
      disabled={disabled}
      aria-label={label}
      className={cn(
        'group relative inline-flex h-8 w-14 shrink-0 cursor-pointer items-center rounded-full bg-line-strong transition-colors',
        'data-checked:bg-primary data-disabled:cursor-not-allowed data-disabled:opacity-60',
        'data-focus:outline-2 data-focus:outline-offset-2 data-focus:outline-primary',
      )}
    >
      <span className="size-6 translate-x-1 rounded-full bg-white shadow transition-transform group-data-checked:translate-x-7" />
    </HeadlessSwitch>
  );
}

export function Checkbox({ className, ...props }: Omit<ComponentProps<'input'>, 'type'>) {
  return (
    <input
      type="checkbox"
      className={cn(
        'size-[18px] shrink-0 cursor-pointer rounded accent-primary disabled:cursor-not-allowed',
        className,
      )}
      {...props}
    />
  );
}

interface CardOption<T extends string> {
  value: T;
  title: ReactNode;
  description?: ReactNode;
}

/** Large choice cards ("Allow, after confirmation" / "Block"). */
export function RadioCards<T extends string>({
  value,
  onChange,
  options,
  label,
  disabled,
  stacked = false,
}: {
  value: T;
  onChange: (value: T) => void;
  options: Array<CardOption<T>>;
  label: string;
  disabled?: boolean;
  /** One option per line (narrow columns). */
  stacked?: boolean;
}) {
  return (
    <RadioGroup
      value={value}
      onChange={onChange}
      aria-label={label}
      disabled={disabled}
      className={cn('grid gap-3', !stacked && 'sm:grid-cols-2')}
    >
      {options.map((option) => (
        <Radio
          key={option.value}
          value={option.value}
          className={cn(
            'group flex cursor-pointer gap-3 rounded-control border border-line-strong bg-surface p-4 transition-colors',
            'data-checked:border-2 data-checked:border-primary data-checked:bg-primary-soft/40',
            'data-focus:outline-2 data-focus:outline-offset-2 data-focus:outline-primary',
            'data-disabled:cursor-not-allowed',
          )}
        >
          <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full border-2 border-line-strong group-data-checked:border-primary">
            <span className="size-2.5 rounded-full bg-primary opacity-0 group-data-checked:opacity-100" />
          </span>
          <span className="min-w-0">
            <span className="block font-semibold text-ink">{option.title}</span>
            {option.description && (
              <span className="mt-0.5 block text-sm text-ink-2">{option.description}</span>
            )}
          </span>
        </Radio>
      ))}
    </RadioGroup>
  );
}
